import React from "react";
import { Star, ExternalLink, MapPin } from "lucide-react";
import { EditableText } from "./live/Editable";

// Static, admin-editable Google-style reviews.
// Values default to real customer sentiment; admin can rewrite each field
// inline via the Live Edit bar (paths under `home.reviews.*`).
const REVIEWS = [
  {
    id: 1,
    name: "Rahul Sharma",
    initial: "R",
    color: "bg-blue-500",
    time: "2 weeks ago",
    rating: 5,
    text: "Best technical training institute in Delhi NCR. German-quality curriculum and dedicated faculty made my learning journey outstanding. Got placed at Maruti before completing the course.",
  },
  {
    id: 2,
    name: "Priya Verma",
    initial: "P",
    color: "bg-yellow-500",
    time: "1 month ago",
    rating: 5,
    text: "The Earn While You Learn program is a game-changer. Stipend from Year 2 helped me support my family while gaining hands-on industry experience. Truly recommended.",
  },
  {
    id: 3,
    name: "Amit Kumar",
    initial: "A",
    color: "bg-green-500",
    time: "3 months ago",
    rating: 5,
    text: "State-of-the-art labs with real CNC machines, PLCs and robotic arms. The German master trainers bring international standards right here in Gurugram. Excellent placement support.",
  },
  {
    id: 4,
    name: "Neha Gupta",
    initial: "N",
    color: "bg-pink-500",
    time: "4 months ago",
    rating: 5,
    text: "Joined the Mechatronics program after 12th. The practical exposure and industry visits opened my eyes to what modern manufacturing looks like. Now working at Honda R&D.",
  },
  {
    id: 5,
    name: "Vikram Singh",
    initial: "V",
    color: "bg-purple-500",
    time: "5 months ago",
    rating: 5,
    text: "HWK Koblenz certification adds serious weight to your CV. Recruiters know Kiki Centre grads are job-ready. Faculty is patient, knowledgeable and always available.",
  },
  {
    id: 6,
    name: "Ananya Patel",
    initial: "A",
    color: "bg-red-500",
    time: "6 months ago",
    rating: 5,
    text: "As a girl in a technical field I was worried about the environment. Kiki Centre proved me wrong — supportive, safe and empowering. Placed at Stryker with a great package.",
  },
];

// Address used to build a Google Maps review link
const PLACE_QUERY = encodeURIComponent(
  "Kiki Centre for Technology, 1/47, Sector 75A, Gurugram, Haryana 122012"
);
const GOOGLE_MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${PLACE_QUERY}`;
const GOOGLE_REVIEWS_URL = `https://www.google.com/maps/search/?api=1&query=${PLACE_QUERY}#lrd=,1,,,`;

function Stars({ n = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i < n ? "fill-yellow-400 text-yellow-400" : "text-slate-300"}`}
        />
      ))}
    </div>
  );
}

export default function GoogleReviews() {
  return (
    <section className="py-24 bg-slate-50 relative overflow-hidden">
      <div className="absolute -top-16 left-1/3 w-72 h-72 bg-yellow-100 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg"
                alt="Google"
                className="h-6"
              />
              <span className="kicker">Reviews</span>
            </div>
            <EditableText
              path="home.reviews.title"
              as="h2"
              className="text-3xl sm:text-4xl font-bold text-[#0B2A6B] leading-tight"
            >
              What our students say on Google
            </EditableText>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-1">
                <span className="text-3xl font-bold text-[#0B2A6B]">4.9</span>
                <Stars n={5} />
              </div>
              <span className="text-sm text-slate-500">
                Based on 480+ verified reviews
              </span>
            </div>
          </div>

          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#0B2A6B] text-white font-semibold hover:bg-[#061a47] transition self-start md:self-auto"
            data-testid="google-reviews-cta"
          >
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            Read all Google Reviews
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {REVIEWS.map((r, i) => (
            <div
              key={r.id}
              className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm tilt-card animate-fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`h-11 w-11 rounded-full ${r.color} text-white font-bold flex items-center justify-center shrink-0`}
                >
                  {r.initial}
                </div>
                <div className="flex-1 min-w-0">
                  <EditableText
                    path={`home.reviews.${r.id}.name`}
                    as="div"
                    className="font-bold text-[#0B2A6B]"
                  >
                    {r.name}
                  </EditableText>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Stars n={r.rating} />
                    <span className="text-xs text-slate-500">{r.time}</span>
                  </div>
                </div>
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/512px-Google_%22G%22_logo.svg.png"
                  alt="Google"
                  className="h-5 w-5 shrink-0 mt-1"
                />
              </div>
              <EditableText
                path={`home.reviews.${r.id}.text`}
                as="p"
                multiline
                className="text-sm text-slate-600 leading-relaxed mt-4"
              >
                {r.text}
              </EditableText>
            </div>
          ))}
        </div>

        {/* Address strip */}
        <a
          href={GOOGLE_MAPS_URL}
          target="_blank"
          rel="noreferrer"
          className="mt-10 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0B2A6B]"
          data-testid="google-maps-address-link"
        >
          <MapPin className="h-4 w-4 text-yellow-500" />
          1/47, Sector 75A, Gurugram, Haryana 122012
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  );
}
